import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogContent,
  DialogTitle, Grid, IconButton, MenuItem, Pagination, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from "@mui/material";
import { ArrowBack, Close, FileDownload, Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AppHeader from "../../components/AppHeader";
import { buildHierarchicalList } from "../../components/staff/constants";
import { buildLeaveHistoryAuditExcelUrl, fetchLeaveHistoryAudit } from "../../services/webReportsService";

const FLAG_LABELS = {
  CL_WITH_OTHER_LEAVE: "CL + Other Leave",
  MIXED_CONSECUTIVE_LEAVE: "Mixed Consecutive Leave",
  SANDWICH_SEQUENCE: "Sandwich Sequence"
};
const STATUSES = ["", "submitted", "pending", "in_review", "approved", "rejected", "cancelled"];
const dateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const displayDate = (value) => value ? String(value).slice(0, 10).split("-").reverse().join("/") : "-";

export default function LeaveHistoryAuditReport() {
  const navigate = useNavigate();
  const tenant = localStorage.getItem("tenant") || "";
  const now = useMemo(() => new Date(), []);
  const [filters, setFilters] = useState({
    date_from: dateKey(new Date(now.getFullYear(), now.getMonth(), 1)),
    date_to: dateKey(now), status: "", flag: "", employee_search: "", branch_id: "", org_node_id: "", leave_type_id: ""
  });
  const [lookups, setLookups] = useState({ branches: [], departments: [], leaveTypes: [] });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, page_size: 25, metrics: { total_records: 0, flagged_records: 0, flags: {} } });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!tenant) return;
    const headers = { "X-Tenant": tenant };
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL1}/som/branches`, { credentials: "include", headers }),
      fetch(`${process.env.REACT_APP_API_URL1}/som/nodes`, { credentials: "include", headers }),
      fetch(`${process.env.REACT_APP_API_URL1}/leave/types`, { credentials: "include", headers })
    ]).then(async (responses) => Promise.all(responses.map((response) => response.json())))
      .then(([branches, nodes, types]) => setLookups({
        branches: branches.data || [],
        departments: buildHierarchicalList(nodes.data || []),
        leaveTypes: types.data || []
      })).catch(() => {});
  }, [tenant]);

  const query = useMemo(() => ({ ...filters, employee_search: filters.employee_search.trim(), page, page_size: 25 }), [filters, page]);
  const load = useCallback(async () => {
    if (!tenant || !query.date_from || !query.date_to) return;
    setLoading(true); setError("");
    try {
      const result = await fetchLeaveHistoryAudit(query);
      setData(result);
    } catch (err) {
      setError(err.message || "Unable to load leave history audit.");
    } finally { setLoading(false); }
  }, [tenant, query]);

  useEffect(() => { load(); }, [load]);
  const update = (name, value) => { setFilters((current) => ({ ...current, [name]: value })); setPage(1); };
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 25)));

  const exportExcel = async () => {
    try {
      setError("");
      const response = await fetch(buildLeaveHistoryAuditExcelUrl(filters), { credentials: "include", headers: { "X-Tenant": tenant } });
      if (!response.ok) throw new Error(await response.text());
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = `Leave_History_Audit_${filters.date_from}_to_${filters.date_to}.xlsx`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
    } catch (err) { setError(err.message || "Export failed."); }
  };

  return <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
    <AppHeader />
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton onClick={() => navigate("/reports")}><ArrowBack /></IconButton>
        <Box><Typography variant="h5" fontWeight={800}>Leave History Audit</Typography>
          <Typography color="text.secondary">Review leave dates, combinations, adjacent rest days and sandwich sequences.</Typography></Box>
      </Stack>

      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
      <Grid container spacing={2} my={1}>
        <Metric title="Leave Requests" value={data.metrics?.total_records || 0} />
        <Metric title="Flagged Requests" value={data.metrics?.flagged_records || 0} color="#dc2626" />
        <Metric title="CL + Other" value={data.metrics?.flags?.CL_WITH_OTHER_LEAVE || 0} color="#d97706" />
        <Metric title="Sandwich Sequences" value={data.metrics?.flags?.SANDWICH_SEQUENCE || 0} color="#7c3aed" />
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ lg: "center" }}>
          <TextField size="small" label="From" type="date" value={filters.date_from} onChange={(e) => update("date_from", e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="To" type="date" value={filters.date_to} onChange={(e) => update("date_to", e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" select label="Status" value={filters.status} onChange={(e) => update("status", e.target.value)} sx={{ minWidth: 150 }}>
            {STATUSES.map((status) => <MenuItem key={status || "all"} value={status}>{status ? status.replace("_", " ").toUpperCase() : "ALL STATUSES"}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Audit Flag" value={filters.flag} onChange={(e) => update("flag", e.target.value)} sx={{ minWidth: 210 }}>
            <MenuItem value="">ALL RECORDS</MenuItem>{Object.entries(FLAG_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Leave Type" value={filters.leave_type_id} onChange={(e) => update("leave_type_id", e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">ALL LEAVE TYPES</MenuItem>{lookups.leaveTypes.map((type) => <MenuItem key={type.id} value={type.id}>{type.type_name}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Branch" value={filters.branch_id} onChange={(e) => update("branch_id", e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">ALL BRANCHES</MenuItem>{lookups.branches.map((branch) => <MenuItem key={branch.id} value={branch.id}>{branch.branch_name}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Department" value={filters.org_node_id} onChange={(e) => update("org_node_id", e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">ALL DEPARTMENTS</MenuItem>{lookups.departments.map((node) => <MenuItem key={node.id} value={node.id}>{`${"  ".repeat(node._depth || 0)}${node.node_name}`}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Employee code or name" value={filters.employee_search} onChange={(e) => update("employee_search", e.target.value)} InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1 }} /> }} />
          <Button variant="outlined" startIcon={<FileDownload />} onClick={exportExcel}>Export Excel</Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        {loading && <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>}
        {!loading && <Table size="small">
          <TableHead><TableRow sx={{ bgcolor: "#eef2ff" }}>
            {["Employee", "Leave", "Dates", "Days", "Status", "Before / After", "Audit Findings", ""].map((label) => <TableCell key={label} sx={{ fontWeight: 800 }}>{label}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>{data.rows?.map((row) => <TableRow key={row.id} hover>
            <TableCell><Typography fontWeight={700}>{row.full_name}</Typography><Typography variant="caption" color="text.secondary">{row.employee_number || row.staff_code} · {row.department_name || "-"}</Typography></TableCell>
            <TableCell>{row.type_name}</TableCell>
            <TableCell>{displayDate(row.start_date)}{row.end_date !== row.start_date ? ` – ${displayDate(row.end_date)}` : ""}</TableCell>
            <TableCell>{row.total_days}</TableCell>
            <TableCell><Chip size="small" label={String(row.status).replace("_", " ")} color={row.status === "approved" ? "success" : row.status === "rejected" ? "error" : "warning"} /></TableCell>
            <TableCell><Typography variant="caption">{row.previous_context?.day_type} / {row.next_context?.day_type}</Typography></TableCell>
            <TableCell><Stack direction="row" flexWrap="wrap" gap={0.5}>{row.flags?.length ? row.flags.map((flag) => <Chip key={flag} size="small" variant="outlined" color="warning" label={FLAG_LABELS[flag] || flag} />) : <Chip size="small" color="success" variant="outlined" label="No finding" />}</Stack></TableCell>
            <TableCell><Button size="small" onClick={() => setSelected(row)}>Details</Button></TableCell>
          </TableRow>)}</TableBody>
        </Table>}
        {!loading && !data.rows?.length && <Typography align="center" color="text.secondary" p={5}>No leave requests match these filters.</Typography>}
      </TableContainer>
      <Stack alignItems="center" mt={2}><Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} /></Stack>
    </Box>
    <DetailDialog row={selected} onClose={() => setSelected(null)} />
  </Box>;
}

function Metric({ title, value, color = "#4338ca" }) {
  return <Grid size={{ xs: 12, sm: 6, md: 3 }}><Card><CardContent><Typography variant="body2" color="text.secondary">{title}</Typography><Typography variant="h4" fontWeight={800} color={color}>{value}</Typography></CardContent></Card></Grid>;
}

function DetailDialog({ row, onClose }) {
  return <Dialog open={Boolean(row)} onClose={onClose} fullWidth maxWidth="sm"><DialogTitle>Leave Request Details<IconButton onClick={onClose} sx={{ float: "right" }}><Close /></IconButton></DialogTitle>
    {row && <DialogContent dividers><Stack spacing={2}>
      <Typography><b>{row.full_name}</b> ({row.employee_number || row.staff_code})</Typography>
      <Typography><b>Leave:</b> {row.type_name}, {displayDate(row.start_date)} to {displayDate(row.end_date)} ({row.total_days} days)</Typography>
      <Typography><b>Reason:</b> {row.reason || "-"}</Typography>
      <Typography><b>Before:</b> {displayDate(row.previous_context?.date)} · {row.previous_context?.day_type}</Typography>
      {row.previous_context?.adjacent_leave_type && <Typography><b>Previous working-day leave:</b> {displayDate(row.previous_context?.adjacent_working_date)} · {row.previous_context.adjacent_leave_type}</Typography>}
      <Typography><b>After:</b> {displayDate(row.next_context?.date)} · {row.next_context?.day_type}</Typography>
      {row.next_context?.adjacent_leave_type && <Typography><b>Next working-day leave:</b> {displayDate(row.next_context?.adjacent_working_date)} · {row.next_context.adjacent_leave_type}</Typography>}
      <Typography><b>Submitted:</b> {row.submitted_by || "-"}</Typography>
      <Typography><b>Reviewed:</b> {row.reviewed_by || "-"} {row.review_action ? `(${row.review_action})` : ""}</Typography>
      <Typography><b>Reviewer comment:</b> {row.reviewer_comment || "-"}</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>{row.flags?.map((flag) => <Chip key={flag} color="warning" label={FLAG_LABELS[flag] || flag} />)}</Stack>
    </Stack></DialogContent>}
  </Dialog>;
}
